import { Injectable, inject } from '@angular/core';
import { Api } from '../api/api';
import {
  adminLearnMoreControllerListLearnMoreV1,
  adminLearnMoreControllerGetLearnMoreV1,
  adminLearnMoreControllerCreateLearnMoreV1,
  adminLearnMoreControllerUpdateLearnMoreV1,
  adminLearnMoreControllerDeleteLearnMoreV1,
  adminLearnMoreControllerReorderLearnMoreV1,
  adminLearnMoreControllerTogglePublishV1,
} from '../api/functions';
import type {
  LearnMoreResponseAdminDto,
  CreateLearnMoreDto,
  UpdateLearnMoreDto,
} from '../api/models';

/**
 * Learn More API Service
 * Wrapper around generated API functions for learn more management
 */
@Injectable({ providedIn: 'root' })
export class LearnMoreApiService {
  private readonly api = inject(Api);

  /**
   * Get all learn more resources (including unpublished)
   */
  async listLearnMore(): Promise<LearnMoreResponseAdminDto[]> {
    return this.api.invoke(adminLearnMoreControllerListLearnMoreV1, {});
  }

  /**
   * Get single learn more resource by ID
   */
  async getLearnMore(id: string): Promise<LearnMoreResponseAdminDto> {
    return this.api.invoke(adminLearnMoreControllerGetLearnMoreV1, { id });
  }

  /**
   * Create new learn more resource with optional cover image
   */
  async createLearnMore(
    data: CreateLearnMoreDto,
    coverImage?: File
  ): Promise<LearnMoreResponseAdminDto> {
    const body: any = {
      title: data.title,
      description: data.description,
      linkUrl: data.linkUrl,
    };

    if (data.order !== undefined) {
      body.order = data.order;
    }

    // Add cover image if provided
    if (coverImage) {
      body.coverImage = coverImage;
    }

    return this.api.invoke(adminLearnMoreControllerCreateLearnMoreV1, {
      body: body,
    });
  }

  /**
   * Update learn more resource with optional new cover image
   */
  async updateLearnMore(
    id: string,
    data: UpdateLearnMoreDto,
    coverImage?: File
  ): Promise<LearnMoreResponseAdminDto> {
    const body: any = {};

    // Add text fields (only if provided)
    if (data.title !== undefined) {
      body.title = data.title;
    }
    if (data.description !== undefined) {
      body.description = data.description;
    }
    if (data.linkUrl !== undefined) {
      body.linkUrl = data.linkUrl;
    }
    if (data.order !== undefined) {
      body.order = data.order;
    }
    if (data.isPublished !== undefined) {
      body.isPublished = data.isPublished;
    }

    // Add cover image if provided
    if (coverImage) {
      body.coverImage = coverImage;
    }

    return this.api.invoke(adminLearnMoreControllerUpdateLearnMoreV1, {
      id,
      body: body,
    });
  }

  /**
   * Delete learn more resource
   */
  async deleteLearnMore(id: string): Promise<void> {
    await this.api.invoke(adminLearnMoreControllerDeleteLearnMoreV1, { id });
  }

  /**
   * Reorder learn more resources
   */
  async reorderLearnMore(
    items: Array<{ id: string; order: number }>
  ): Promise<LearnMoreResponseAdminDto[]> {
    return this.api.invoke(adminLearnMoreControllerReorderLearnMoreV1, {
      body: { items },
    });
  }

  /**
   * Toggle publish status
   */
  async togglePublish(id: string, isPublished: boolean): Promise<LearnMoreResponseAdminDto> {
    return this.api.invoke(adminLearnMoreControllerTogglePublishV1, {
      id,
      body: { isPublished },
    });
  }

  /**
   * Get cover image URL for display
   */
  getCoverImageUrl(fileId: string): string {
    if (!fileId) return '';
    // Assuming the backend serves files at /api/v1/files/file/:id
    return `${this.api.rootUrl}/v1/files/file/${fileId}`;
  }
}
